/**
 * Interface localization.
 *
 * English and Spanish are **both first-class at launch** — Spanish is not a translation added later,
 * and `npm run i18n:check` fails when a launch-critical namespace has a key in one and not the other
 * (12-localization.md, ADR-017).
 *
 * ## Bundled, not lazy-loaded
 *
 * The spec allows lazy namespaces so the Participant route need not carry every administrative
 * string. At two languages and a few hundred keys the whole catalogue is smaller than one of the
 * screens that would load it, and a network round-trip for chrome text is the wrong trade on the
 * surface that matters most: a family opening a link on a phone, on a bad connection, at a funeral.
 * Split when it is genuinely large; the namespace structure is already right for it.
 *
 * ## The fallback chain
 *
 * `es → en → never a raw key.` A missing key renders the English string and warns in development. A
 * participant must never see `participate.item.not_set` on screen — that is worse than being in the
 * wrong language, because the wrong language is still a sentence.
 */

import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { LAUNCH_LANGUAGES, negotiateLanguage, type LaunchLanguage } from '@pius/contracts';

import enCommon from './en/common.json';
import enErrors from './en/errors.json';
import enParticipate from './en/participate.json';
import enPreferences from './en/preferences.json';
import esCommon from './es/common.json';
import esErrors from './es/errors.json';
import esParticipate from './es/participate.json';
import esPreferences from './es/preferences.json';

export const DEFAULT_LANGUAGE: LaunchLanguage = 'en';

const resources = {
  en: {
    common: enCommon,
    errors: enErrors,
    participate: enParticipate,
    preferences: enPreferences,
  },
  es: {
    common: esCommon,
    errors: esErrors,
    participate: esParticipate,
    preferences: esPreferences,
  },
} as const;

/**
 * What language to start in.
 *
 * `negotiateLanguage` lives in `@pius/contracts` and is tested there: **most people reading this
 * application will never have an Account**, so the browser's own list is the primary signal rather
 * than a fallback. The Account preference overrides it once `/me/preferences` answers — for the
 * people who have one.
 */
function initialLanguage(): LaunchLanguage {
  return negotiateLanguage(
    globalThis.localStorage?.getItem('pius.language'),
    globalThis.navigator?.languages ?? [],
  );
}

void i18next.use(initReactI18next).init({
  resources,
  lng: initialLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  supportedLngs: [...LAUNCH_LANGUAGES],
  defaultNS: 'common',
  ns: ['common', 'errors', 'participate', 'preferences'],

  interpolation: {
    // React escapes for us. Double-escaping turns an apostrophe in "Jesu, Joy of Man's Desiring"
    // into `&amp;#39;` on screen.
    escapeValue: false,
  },

  // Loud in development, silent in production — a missing key is a bug to fix, not a runtime error
  // to show somebody planning a funeral.
  saveMissing: false,
  missingKeyHandler: import.meta.env.DEV
    ? (_languages: readonly string[], namespace: string, key: string) => {
        console.warn(`i18n: missing key ${namespace}:${key}`);
      }
    : false,

  react: { useSuspense: false },
});

/** Changes the language and remembers it, so a reload does not revert to the browser's guess. */
export async function setLanguage(language: LaunchLanguage): Promise<void> {
  globalThis.localStorage?.setItem('pius.language', language);
  document.documentElement.lang = language;
  await i18next.changeLanguage(language);
}

// Set once at startup so assistive technology announces the page in the right language from the
// first paint, rather than after the first change.
document.documentElement.lang = i18next.language;

export { i18next };
